import { DriverData, EnhancedDriverData } from '../types/driverData'
import { BudgetDriver } from '../../../types/budgetDriver/BudgetDriver'
import { BalanceMap, BalanceGroupMap } from '../../financialStatements/types'
import { VariableType } from '../../../components/Table/types'

/**
 * Rounds all values in a BalanceMap to specified decimal precision
 * @param registry - BalanceMap with number keys and number|null values
 * @param decimals - Number of decimal places
 * @returns Rounded BalanceMap
 */
const roundBalanceMap = (registry: BalanceMap, decimals: number): BalanceMap => {
  const rounded: BalanceMap = {}
  Object.entries(registry).forEach(([key, value]) => {
    rounded[Number(key)] = value === null ? null : Number(value.toFixed(decimals))
  })
  return rounded
}

/**
 * Rounds all values in a BalanceGroupMap to specified decimal precision
 * @param registry - BalanceGroupMap with string|number keys and number|null values
 * @param decimals - Number of decimal places
 * @returns Rounded BalanceGroupMap
 */
const roundBalanceGroupMap = (registry: BalanceGroupMap, decimals: number): BalanceGroupMap => {
  const rounded: BalanceGroupMap = {}
  Object.entries(registry).forEach(([key, value]) => {
    rounded[key] = value === null ? null : Number(value.toFixed(decimals))
  })
  return rounded
}

/**
 * Enhances driver data with budget driver and dimension names for display purposes
 * Also rounds budget values to budget driver's decimal precision to fix floating point errors
 *
 * @param data - Raw driver data from backend
 * @param budgetDriverMap - Map of formulaId -> BudgetDriver object
 * @param dimensionMap - Map of dimensionId -> dimension name
 * @returns Enhanced driver data with names and formatting info
 */
export const enhanceDriverData = (
  data: DriverData[],
  budgetDriverMap: Map<number, BudgetDriver>,
  dimensionMap: Map<string, string>
): EnhancedDriverData[] => {
  return data.map(row => {
    const budgetDriver = budgetDriverMap.get(row.formulaId)
    const formula = budgetDriver?.formula
    const decimals = formula?.style?.decimals
    const { budget: originalBudget } = row

    // Round budget values if decimals are specified
    // For percentages, add +2 decimals because backend stores as decimal (0.055 = 5.5%)
    const budget =
      originalBudget && decimals !== undefined
        ? {
            ...originalBudget,
            elements: roundBalanceMap(
              originalBudget.elements,
              formula?.type === VariableType.percentage ? decimals + 2 : decimals
            ),
            groups: roundBalanceGroupMap(
              originalBudget.groups,
              formula?.type === VariableType.percentage ? decimals + 2 : decimals
            )
          }
        : originalBudget

    return {
      ...row,
      budget,
      driverName: budgetDriver?.name || `Driver ${row.formulaId}`,
      dimensionName: dimensionMap.get(row.dimensionId) || row.dimensionId,
      style: decimals !== undefined ? { decimals } : undefined
    }
  })
}
